import { useFlightPlansQuery } from "@/api/v1/flight-plan-api";
import { FlightPlansTable } from "@/components/flight-plan/table/data-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Plane, AlertCircle, Info } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { Skeleton } from "@/components/ui/skeleton";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useFeatureAccess } from "@/hooks/use-subscription-limits";

export const FlightPlansRoute = () => {
  const { data: flightPlans, isLoading, error } = useFlightPlansQuery();
  const navigate = useNavigate();
  const totalFlightPlans = flightPlans?.length ?? 0;
  
  const { limit, isUnlimited: isUnlimitedFlightPlans } = useFeatureAccess(
    "flight_plans",
    totalFlightPlans
  );
  
  const hasReachedLimit = !isUnlimitedFlightPlans && limit > 0 && totalFlightPlans >= limit;

  return (
    <main>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        <BreadcrumbNav
          items={[{ label: "Home", href: "/" }, { label: "Flight Plans" }]}
          className="mb-6"
        />

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold">Flight Plans</h1>
            <p className="text-sm text-muted-foreground">
              Manage and monitor your flight plans
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => navigate({ to: "/flight-plan/create" as any })}
              disabled={hasReachedLimit}
            >
              <Plus className="h-4 w-4 mr-2" />
              New Flight Plan
            </Button>
            {hasReachedLimit && (
              <Badge variant="outline" className="w-fit">
                <Info className="h-3 w-3 mr-1" />
                {`${totalFlightPlans}/${limit} Limit Reached`}
              </Badge>
            )}
            {!hasReachedLimit && !isUnlimitedFlightPlans && limit > 0 && (
              <Badge variant="secondary" className="w-fit">
                <Info className="h-3 w-3 mr-1" />
                {totalFlightPlans}/{limit} Used
              </Badge>
            )}
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Failed to load flight plans. Please try again later.
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Recent Plans</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : flightPlans?.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Plane className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>No flight plans found</p>
                <Button
                  variant="link"
                  onClick={() => navigate({ to: "/flight-plan/create" as any })}
                >
                  Create your first plan
                </Button>
              </div>
            ) : (
              <FlightPlansTable data={flightPlans || []} />
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
};
